// Superuser-only management panel: clients, users, project->client assignment, backups.
// Opened from the topbar "Manage" button (gated to Viewport superusers). All calls hit the
// /api/admin/* routes, which the backend middleware restricts to superusers.
import { api, type AdminClient, type AdminUser, type Project } from "../api";
import { toast } from "./toast";

let isOpen = false;

export async function openAdminPanel(): Promise<void> {
  if (isOpen) return;
  isOpen = true;
  const overlay = document.createElement("div");
  overlay.className = "admin-overlay";
  overlay.innerHTML = `
    <div class="admin-card">
      <div class="admin-head">
        <span class="admin-title">Manage · Clients, Users &amp; Backups</span>
        <button class="tool-btn admin-close" type="button">Close</button>
      </div>
      <div class="admin-body"><div class="admin-msg">Loading…</div></div>
    </div>`;
  document.body.append(overlay);
  const close = () => { overlay.remove(); isOpen = false; };
  overlay.querySelector(".admin-close")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  document.addEventListener("keydown", function esc(e) {
    if (e.key === "Escape") { close(); document.removeEventListener("keydown", esc); }
  });
  await render(overlay.querySelector(".admin-body") as HTMLElement);
}

async function render(body: HTMLElement): Promise<void> {
  body.innerHTML = `<div class="admin-msg">Loading…</div>`;
  let clients: AdminClient[];
  let users: AdminUser[];
  let projects: Project[];
  let backups: { name: string; size: number; mtime: number }[];
  try {
    [clients, users, projects, backups] = await Promise.all([
      api.listClients().then((r) => r.clients),
      api.listUsers().then((r) => r.users),
      api.listProjects(),
      api.listBackups().then((r) => r.backups),
    ]);
  } catch (e) {
    body.innerHTML = `<div class="admin-msg err">Failed to load: ${(e as Error).message}</div>`;
    return;
  }
  const refresh = () => void render(body);
  body.innerHTML = "";
  body.append(
    clientsSection(clients, refresh),
    usersSection(users, clients, refresh),
    projectsSection(projects, clients, refresh),
    backupsSection(backups, refresh),
  );
}

// --- helpers ---
function section(title: string): HTMLElement {
  const s = document.createElement("div");
  s.className = "admin-section";
  const h = document.createElement("div");
  h.className = "admin-section-title";
  h.textContent = title;
  s.append(h);
  return s;
}

function clientOptions(clients: AdminClient[], selected?: number | null): string {
  return clients
    .map((c) => `<option value="${c.id}" ${c.id === selected ? "selected" : ""}>${c.name}${c.is_internal ? " (internal)" : ""}</option>`)
    .join("");
}

async function guard(fn: () => Promise<unknown>, ok: string, refresh: () => void): Promise<void> {
  try {
    await fn();
    toast(ok, "ok");
    refresh();
  } catch (e) {
    toast((e as Error).message, "warn", 6000);
  }
}

// --- Clients ---
function clientsSection(clients: AdminClient[], refresh: () => void): HTMLElement {
  const s = section("Clients");
  const table = document.createElement("table");
  table.className = "admin-table";
  table.innerHTML =
    "<tr><th>Name</th><th>Slug</th><th>Projects</th><th>Users</th><th>Type</th></tr>" +
    clients
      .map((c) => `<tr><td>${c.name}</td><td>${c.slug}</td><td>${c.projects}</td><td>${c.users}</td><td>${c.is_internal ? "internal" : "client"}</td></tr>`)
      .join("");
  s.append(table);

  const row = document.createElement("div");
  row.className = "admin-row";
  row.innerHTML = `
    <input class="admin-input" placeholder="New client name (e.g. Fortescue)" />
    <button class="tool-btn" type="button">Add client</button>`;
  const input = row.querySelector("input")!;
  row.querySelector("button")!.addEventListener("click", () => {
    const name = input.value.trim();
    if (!name) return;
    void guard(() => api.createClient(name), `Client "${name}" created`, refresh);
  });
  s.append(row);
  return s;
}

// --- Users ---
function usersSection(users: AdminUser[], clients: AdminClient[], refresh: () => void): HTMLElement {
  const s = section("Users");
  const table = document.createElement("table");
  table.className = "admin-table";
  const head = "<tr><th>Username</th><th>Client</th><th>Role</th><th>Status</th><th></th></tr>";
  table.innerHTML =
    head +
    users
      .map(
        (u) => `<tr data-uid="${u.id}">
          <td>${u.username}${u.is_superuser ? " ★" : ""}</td>
          <td>${u.client_name ?? "-"}</td>
          <td>${u.role}</td>
          <td>${u.active ? "active" : "disabled"}</td>
          <td>
            <button class="admin-mini" data-act="pw">Reset pw</button>
            <button class="admin-mini" data-act="toggle">${u.active ? "Disable" : "Enable"}</button>
          </td></tr>`,
      )
      .join("");
  table.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const uid = Number(btn.closest("tr")!.getAttribute("data-uid"));
      const u = users.find((x) => x.id === uid)!;
      if (btn.dataset.act === "pw") {
        const pw = window.prompt(`New password for ${u.username}:`);
        if (pw) void guard(() => api.updateUser(uid, { password: pw }), "Password reset", refresh);
      } else {
        void guard(() => api.updateUser(uid, { active: !u.active }), u.active ? "User disabled" : "User enabled", refresh);
      }
    });
  });
  s.append(table);

  const row = document.createElement("div");
  row.className = "admin-row";
  row.innerHTML = `
    <input class="admin-input" data-f="username" placeholder="username" />
    <input class="admin-input" data-f="password" type="password" placeholder="password" />
    <select class="admin-input" data-f="client">${clientOptions(clients)}</select>
    <select class="admin-input" data-f="role"><option value="viewer">viewer</option><option value="admin">admin</option></select>
    <button class="tool-btn" type="button">Add user</button>`;
  row.querySelector("button")!.addEventListener("click", () => {
    const get = (f: string) => (row.querySelector(`[data-f="${f}"]`) as HTMLInputElement | HTMLSelectElement).value;
    const username = get("username").trim();
    const password = get("password");
    const client_id = Number(get("client"));
    if (!username || !password || !client_id) {
      toast("username, password and client are required", "warn");
      return;
    }
    void guard(
      () => api.createUser({ username, password, client_id, role: get("role") }),
      `User "${username}" created`,
      refresh,
    );
  });
  s.append(row);
  return s;
}

// --- Projects (assign to client) ---
function projectsSection(projects: Project[], clients: AdminClient[], refresh: () => void): HTMLElement {
  const s = section("Projects · assign to client");
  const table = document.createElement("table");
  table.className = "admin-table";
  table.innerHTML = "<tr><th>Project</th><th>Slug</th><th>Client</th></tr>";
  for (const p of projects) {
    const tr = document.createElement("tr");
    const sel = document.createElement("select");
    sel.className = "admin-input";
    sel.innerHTML = clientOptions(clients, p.client_id);
    sel.addEventListener("change", () => {
      void guard(() => api.assignProject(p.id, Number(sel.value)), `"${p.name}" reassigned`, refresh);
    });
    const tdName = document.createElement("td");
    tdName.textContent = p.name;
    const tdSlug = document.createElement("td");
    tdSlug.textContent = p.slug;
    const tdSel = document.createElement("td");
    tdSel.append(sel);
    tr.append(tdName, tdSlug, tdSel);
    table.append(tr);
  }
  s.append(table);
  return s;
}

// --- Backups ---
function backupsSection(
  backups: { name: string; size: number; mtime: number }[],
  refresh: () => void,
): HTMLElement {
  const s = section("Backups");
  const note = document.createElement("div");
  note.className = "admin-note";
  note.textContent =
    "Creates a consistent snapshot of the database plus reports and screenshots. Restore is run from the CLI (python -m app.backup) with the app stopped.";
  s.append(note);

  const table = document.createElement("table");
  table.className = "admin-table";
  table.innerHTML =
    "<tr><th>Backup</th><th>Size</th><th></th></tr>" +
    (backups.length
      ? backups
          .map(
            (b) => `<tr><td>${b.name}</td><td>${(b.size / 1e6).toFixed(1)} MB</td>
              <td><a class="admin-mini" href="/api/admin/backups/${b.name}" download>Download</a></td></tr>`,
          )
          .join("")
      : "<tr><td colspan='3'>(no backups yet)</td></tr>");
  s.append(table);

  const row = document.createElement("div");
  row.className = "admin-row";
  row.innerHTML = `
    <label class="admin-check"><input type="checkbox" data-f="rasters" /> include rasters (large)</label>
    <button class="tool-btn" type="button">Create backup</button>`;
  const btn = row.querySelector("button")!;
  btn.addEventListener("click", () => {
    const rasters = (row.querySelector('[data-f="rasters"]') as HTMLInputElement).checked;
    btn.disabled = true;
    btn.textContent = "Backing up…";
    void guard(() => api.createBackup(rasters), "Backup created", refresh).finally(() => {
      btn.disabled = false;
      btn.textContent = "Create backup";
    });
  });
  s.append(row);
  return s;
}
